import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, Loader2 } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Cambiar la contraseña desde el perfil. Pide primero la actual. Al cambiarla se cierran
  las sesiones abiertas en otros dispositivos; esta sigue abierta con un token nuevo.
*/
const RULES = [
  [(v) => v.length >= 8, 'len'],
  [(v) => /[A-Z]/.test(v), 'upper'],
  [(v) => /[0-9]/.test(v), 'digit'],
];

const T = {
  es: {
    rules: { len: '8 caracteres', upper: 'una mayúscula', digit: 'un número' },
    done: 'Contraseña actualizada. Las sesiones en otros dispositivos se han cerrado.',
    title: 'Contraseña',
    hint: 'Cámbiala cuando quieras; te pediremos la actual.',
    change: 'Cambiar',
    current: 'Contraseña actual',
    next: 'Contraseña nueva',
    repeat: 'Repite la contraseña nueva',
    mismatch: 'No coinciden',
    save: 'Guardar contraseña',
    cancel: 'Cancelar',
    hide: 'Ocultar contraseñas',
    show: 'Mostrar contraseñas',
  },
  en: {
    rules: { len: '8 characters', upper: 'one capital letter', digit: 'one number' },
    done: 'Password updated. You’ve been signed out on your other devices.',
    title: 'Password',
    hint: 'Change it whenever you like; we’ll ask for your current one.',
    change: 'Change',
    current: 'Current password',
    next: 'New password',
    repeat: 'Repeat the new password',
    mismatch: 'They don’t match',
    save: 'Save password',
    cancel: 'Cancel',
    hide: 'Hide passwords',
    show: 'Show passwords',
  },
};

export function ChangePasswordCard({ delay = 0.15 }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;

  const okRules = RULES.every(([f]) => f(next));
  const canSubmit = current && okRules && next === repeat && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const r = await dashboardApi.changePassword(current, next);
      if (r.token) sessionStorage.setItem('dashboard_token', r.token);
      toast.success(t.done);
      setCurrent(''); setNext(''); setRepeat(''); setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a84c]/50';
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay }}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid="change-password-card">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20 flex items-center justify-center">
          <KeyRound size={16} className="text-[#c9a84c]" />
        </div>
        <div className="flex-1">
          <p className="text-white font-medium text-sm">{t.title}</p>
          <p className="text-xs text-gray-400">{t.hint}</p>
        </div>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} data-testid="change-password-open"
            className="px-3 py-1.5 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white hover:border-[#c9a84c]/40">
            {t.change}
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-3" autoComplete="on">
          <label className="block">
            <span className="text-xs text-gray-400">{t.current}</span>
            <input type={show ? 'text' : 'password'} autoComplete="current-password" value={current}
              onChange={(e) => setCurrent(e.target.value)} className={input} data-testid="pw-current" required />
          </label>
          <label className="block">
            <span className="text-xs text-gray-400">{t.next}</span>
            <input type={show ? 'text' : 'password'} autoComplete="new-password" value={next}
              onChange={(e) => setNext(e.target.value)} className={input} data-testid="pw-new" required />
          </label>
          <label className="block">
            <span className="text-xs text-gray-400">{t.repeat}</span>
            <input type={show ? 'text' : 'password'} autoComplete="new-password" value={repeat}
              onChange={(e) => setRepeat(e.target.value)} className={input} data-testid="pw-repeat" required />
          </label>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
            {RULES.map(([f, key]) => (
              <span key={key} className={next && f(next) ? 'text-emerald-300' : 'text-gray-500'}>✓ {t.rules[key]}</span>
            ))}
            {repeat && next !== repeat && <span className="text-red-300">{t.mismatch}</span>}
          </div>
          {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={!canSubmit} data-testid="pw-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c9a84c] text-[#060d1b] text-sm font-semibold disabled:opacity-40">
              {busy && <Loader2 size={14} className="animate-spin" />} {t.save}
            </button>
            <button type="button" onClick={() => { setOpen(false); setError(null); }} className="px-3 py-2 text-sm text-gray-400 hover:text-white">{t.cancel}</button>
            <button type="button" onClick={() => setShow((s) => !s)} className="ml-auto text-gray-400 hover:text-white" aria-label={show ? t.hide : t.show}>
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );
}
